import {
  Bleed,
  Box,
  Column,
  Columns,
  Hidden,
  PageBlock,
  Stack,
} from 'braid-design-system';
import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import { Helmet } from 'react-helmet';
import {
  Breadcrumbs,
  type Breadcrumb,
} from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import { navHeightTabletDesktop } from '#src/styles.css.ts';
import { Footer } from '../Footer/Footer.tsx';
import { ImpersonationBanner } from '../ImpersonationBanner/ImpersonationBanner.tsx';
import { PageTitle } from '../PageTitle/PageTitle.tsx';
import * as styles from './BasePage.css';

import { Menu } from '#src/App/Menu.tsx';
import { Nav } from '#src/App/Nav.tsx';
import {
  PreviewDeploymentBanner,
  useShowPreview,
} from '#src/App/PreviewDeploymentBanner.tsx';
import { ElementPositionProvider } from '#src/hooks/ElementPositionProvider.tsx';
import { GlobalButtonTracker } from '#src/hooks/GlobalButtonTracker.tsx';
import { SiteSearch } from '../SiteSearch/SiteSearch.tsx';

type BaseProps = {
  breadcrumbs: Breadcrumb[] | null;
  children: ComponentProps<typeof Stack>['children'];
  sidebar?: ReactNode;
  unmaintainablyEjectFromAConsistentLayout?: boolean;
};

type Props = BaseProps &
  (
    | {
        title: string | null;
        icon?: ComponentProps<typeof PageTitle>['icon'];
      }
    | {
        title: ComponentProps<typeof PageTitle> & {
          stringTitle: string;
        };
        icon?: never;
      }
  );

const Layout = ({
  breadcrumbs,
  children,
}: {
  breadcrumbs: Breadcrumb[] | null;
  children: ReactNode;
}) => {
  const [stickyHeight, setStickyHeight] = useState<string>(
    navHeightTabletDesktop,
  );
  const stickyRef = useRef<HTMLElement>(null);

  useEffect(() => {
    const recalculate = () => {
      if (stickyRef.current) {
        const measuredHeight = `${stickyRef.current.clientHeight}px`;
        if (measuredHeight !== stickyHeight) {
          setStickyHeight(measuredHeight);
        }
      }
    };

    recalculate();

    const observer = new ResizeObserver(recalculate);
    const element = stickyRef.current;

    if (element) {
      observer.observe(element);
    }

    return () => {
      if (element) {
        observer.unobserve(element);
      }
    };
  }, [stickyHeight]);

  const [showPreview, dismiss] = useShowPreview();

  const [open, setOpen] = useState(false);
  const close = useCallback(() => setOpen(false), []);
  const onForceOpen = useCallback(() => setOpen(true), []);

  return (
    <Box display="flex" flexDirection="column">
      <ElementPositionProvider position="site header">
        <Box
          component="header"
          style={{ position: 'sticky' }}
          width="full"
          zIndex="sticky"
          top={0}
          ref={stickyRef}
        >
          {showPreview ? <PreviewDeploymentBanner dismiss={dismiss} /> : null}
          <Nav setMenuOpened={setOpen} />
        </Box>
      </ElementPositionProvider>

      <Columns space="none">
        <Column width="content">
          <ElementPositionProvider position="left menu">
            <Menu
              close={close}
              isOpen={open}
              onForceOpen={onForceOpen}
              usedHeight={stickyHeight}
              breadcrumbs={breadcrumbs}
            />
          </ElementPositionProvider>
        </Column>

        <Column>
          <ElementPositionProvider position="main body">
            <GlobalButtonTracker>
              <Box width="full">{children}</Box>
            </GlobalButtonTracker>
          </ElementPositionProvider>
        </Column>
      </Columns>
    </Box>
  );
};

export const BasePage = ({
  breadcrumbs,
  children,
  title: header,
  icon,
  sidebar,
  unmaintainablyEjectFromAConsistentLayout,
}: Props) => (
  <Layout breadcrumbs={breadcrumbs}>
    <Stack space="none">
      <ImpersonationBanner />

      {header ? (
        <Helmet>
          <title>
            {typeof header === 'string' ? header : header.stringTitle}
          </title>
        </Helmet>
      ) : null}

      {unmaintainablyEjectFromAConsistentLayout ? (
        children
      ) : (
        <PageBlock width="large">
          <Columns space="none">
            <Column>
              <Box
                display="flex"
                flexDirection="column"
                justifyContent="spaceBetween"
                gap="xlarge"
                paddingTop="large"
                className={styles.minHeightFull}
              >
                <Stack space="large">
                  <Stack space="medium">
                    <Hidden above="desktop">
                      <Stack space="small">
                        {breadcrumbs && breadcrumbs.length > 1 ? (
                          <Bleed top="small">
                            <Breadcrumbs breadcrumbs={breadcrumbs} />
                          </Bleed>
                        ) : null}
                        <Hidden below="tablet">
                          <Box
                            paddingTop={
                              breadcrumbs && breadcrumbs.length > 1
                                ? 'none'
                                : 'small'
                            }
                          >
                            <SiteSearch inline />
                          </Box>
                        </Hidden>
                      </Stack>
                    </Hidden>
                    {typeof header === 'string' ? (
                      <PageTitle icon={icon}>{header}</PageTitle>
                    ) : (
                      <PageTitle {...header} />
                    )}
                  </Stack>
                  <Stack component="main" space="xlarge">
                    {children}
                  </Stack>
                </Stack>
                <Footer />
              </Box>
            </Column>

            {sidebar ? <Column width="content">{sidebar}</Column> : null}
          </Columns>
        </PageBlock>
      )}
    </Stack>
  </Layout>
);
